import { useEffect, useRef, useState } from "react";
import { Loader2, Plus, Trash2, Pencil, Upload, Newspaper, Sparkles } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { fullUrl } from "@/context/SiteConfigContext";
import { PageHeader } from "@/components/admin/ui";

const inputCls =
  "w-full rounded-xl border border-white/10 bg-[#0A0B0D] px-4 py-3 text-sm text-white placeholder-zinc-500 outline-none focus:border-[#FFB800]";

const EMPTY = { title: "", category: "Dicas", date: "", excerpt: "", paragraphsText: "", cta: "Agendar Serviço", img: "" };

export default function BlogPage() {
  const [posts, setPosts] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [topic, setTopic] = useState("");
  const fileInput = useRef(null);

  const generateDraft = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/blog/generate", { topic });
      setForm((f) => ({
        ...f,
        title: data.title || f.title,
        category: data.category || f.category,
        excerpt: data.excerpt || f.excerpt,
        paragraphsText: (data.paragraphs || []).join("\n\n"),
        cta: data.cta || f.cta,
      }));
      toast.success("Rascunho gerado! Revise e publique quando quiser.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setGenerating(false);
    }
  };

  const load = () => api.get("/blog").then((r) => setPosts(r.data)).catch(() => setPosts([]));
  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY);
    setOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      title: p.title,
      category: p.category,
      date: p.date,
      excerpt: p.excerpt,
      paragraphsText: (p.paragraphs || []).join("\n\n"),
      cta: p.cta,
      img: p.img || "",
    });
    setOpen(true);
  };

  const uploadCover = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd);
      setForm((f) => ({ ...f, img: data.url }));
      toast.success("Capa enviada!");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      title: form.title,
      category: form.category,
      date: form.date,
      excerpt: form.excerpt,
      paragraphs: form.paragraphsText.split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean),
      cta: form.cta,
      img: form.img,
    };
    try {
      if (editing) {
        await api.put(`/blog/${editing.id}`, payload);
        toast.success("Artigo atualizado!");
      } else {
        await api.post("/blog", payload);
        toast.success("Artigo publicado no site!");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    try {
      await api.delete(`/blog/${p.id}`);
      toast.success("Artigo removido");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (!posts) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 size={28} className="animate-spin text-[#FFB800]" />
      </div>
    );
  }

  return (
    <div data-testid="blog-page">
      <PageHeader title="Blog & Dicas" sub={`${posts.length} artigo(s) publicado(s) na seção Dicas do site`} testId="blog-header">
        <button
          onClick={openNew}
          data-testid="blog-new-button"
          className="flex items-center gap-2 rounded-full bg-[#FFB800] px-5 py-2.5 font-display text-sm font-bold uppercase tracking-wider text-black transition-transform hover:scale-105"
        >
          <Plus size={16} strokeWidth={3} /> Novo Artigo
        </button>
      </PageHeader>

      {posts.length === 0 ? (
        <div className="flex flex-col items-center gap-4 rounded-2xl border border-dashed border-white/10 bg-[#11141A] py-20 text-center" data-testid="blog-empty">
          <Newspaper size={40} className="text-zinc-600" />
          <p className="text-sm text-zinc-500">Nenhum artigo ainda. Publique sua primeira dica!</p>
        </div>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2 xl:grid-cols-3" data-testid="blog-list">
          {posts.map((p, i) => (
            <div key={p.id} className="overflow-hidden rounded-2xl border border-white/5 bg-[#11141A]" data-testid={`blog-item-${i}`}>
              {p.img && <img src={fullUrl(p.img)} alt={p.title} className="aspect-[1000/650] w-full object-cover" />}
              <div className="p-5">
                <div className="flex items-center gap-3 text-[11px] text-zinc-500">
                  <span className="font-semibold uppercase tracking-wide text-[#FFB800]">{p.category}</span>
                  <span>{p.date}</span>
                </div>
                <h3 className="mt-2 font-display text-base font-bold uppercase leading-tight text-white">{p.title}</h3>
                <p className="mt-1.5 line-clamp-2 text-xs leading-relaxed text-zinc-500">{p.excerpt}</p>
                <div className="mt-4 flex items-center justify-between">
                  <a href={`/dicas/${p.slug}`} target="_blank" rel="noreferrer" className="text-xs font-semibold text-zinc-400 transition-colors hover:text-[#FFB800]" data-testid={`blog-view-${i}`}>
                    ver no site →
                  </a>
                  <div className="flex gap-1">
                    <button onClick={() => openEdit(p)} data-testid={`blog-edit-${i}`} className="flex h-8 w-8 items-center justify-center rounded-full text-zinc-400 transition-colors hover:bg-white/5 hover:text-[#FFB800]" aria-label="Editar">
                      <Pencil size={14} />
                    </button>
                    <button onClick={() => remove(p)} data-testid={`blog-delete-${i}`} className="flex h-8 w-8 items-center justify-center rounded-full text-zinc-500 transition-colors hover:bg-red-500/10 hover:text-red-400" aria-label="Remover">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[88vh] max-w-2xl overflow-y-auto border-white/10 bg-[#11141A] text-white" data-testid="blog-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-bold uppercase">
              {editing ? "Editar" : "Novo"} <span className="italic text-[#FFB800]">Artigo</span>
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4 pt-2">
            <div className="rounded-xl border border-[#FFB800]/25 bg-[#FFB800]/5 p-4" data-testid="ai-draft-box">
              <label className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#FFB800]">
                <Sparkles size={13} /> Gerar rascunho com IA
              </label>
              <div className="mt-2.5 flex gap-2">
                <input
                  data-testid="ai-topic-input"
                  placeholder="Tema (opcional) — ex: sinais de vazamento em apartamento"
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  className={inputCls}
                />
                <button
                  type="button"
                  onClick={generateDraft}
                  disabled={generating}
                  data-testid="ai-generate-button"
                  className="flex shrink-0 items-center gap-2 rounded-full bg-[#FFB800] px-5 py-2.5 font-display text-xs font-bold uppercase tracking-wider text-black transition-transform hover:scale-105 disabled:opacity-60"
                >
                  {generating ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                  {generating ? "Gerando..." : "Gerar"}
                </button>
              </div>
              <p className="mt-2 text-[11px] text-zinc-500">A IA preenche título, resumo e texto — você revisa antes de publicar.</p>
            </div>
            <input required data-testid="blog-title-input" placeholder="Título do artigo *" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} />
            <div className="grid gap-4 sm:grid-cols-2">
              <input data-testid="blog-category-input" placeholder="Categoria (ex: Tubulação de gás)" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={inputCls} />
              <input data-testid="blog-date-input" placeholder="Data (ex: 14/08/2025) — vazio = hoje" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={inputCls} />
            </div>
            <textarea required data-testid="blog-excerpt-input" rows={2} placeholder="Resumo que aparece no card *" value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} className={`${inputCls} resize-none`} />
            <div>
              <textarea required data-testid="blog-content-input" rows={7} placeholder="Texto completo — separe os parágrafos com uma linha em branco *" value={form.paragraphsText} onChange={(e) => setForm({ ...form, paragraphsText: e.target.value })} className={`${inputCls} resize-none`} />
            </div>
            <div className="flex items-center gap-4">
              <input ref={fileInput} type="file" accept="image/*" onChange={uploadCover} className="hidden" data-testid="blog-cover-input" />
              <button type="button" onClick={() => fileInput.current?.click()} disabled={uploading} data-testid="blog-cover-button" className="flex items-center gap-2 rounded-full border border-[#FFB800]/50 px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-[#FFB800] transition-colors hover:bg-[#FFB800]/10 disabled:opacity-60">
                {uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
                {form.img ? "Trocar capa" : "Enviar capa"}
              </button>
              {form.img && <img src={fullUrl(form.img)} alt="Capa" className="h-12 w-20 rounded-lg object-cover" data-testid="blog-cover-preview" />}
            </div>
            <input data-testid="blog-cta-input" placeholder="Texto do botão (ex: Agendar Serviço)" value={form.cta} onChange={(e) => setForm({ ...form, cta: e.target.value })} className={inputCls} />
            <button type="submit" disabled={saving} data-testid="blog-submit-button" className="flex w-full items-center justify-center gap-2 rounded-full bg-[#FFB800] py-3.5 font-display text-sm font-bold uppercase tracking-wider text-black transition-transform hover:scale-[1.02] disabled:opacity-60">
              {saving && <Loader2 size={16} className="animate-spin" />}
              {saving ? "Salvando..." : editing ? "Salvar Alterações" : "Publicar Artigo"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
